/**
 * PerformanceCard Component - Enhanced performance settings card with modern UI
 */
import { __ } from '@wordpress/i18n';
import {
    Card,
    Switch,
    Space,
    Typography,
    Row,
    Col,
    Tooltip,
    Progress,
    Statistic,
    Badge,
    Avatar,
    Tag,
    Alert
} from 'antd';
import {
    ThunderboltOutlined,
    RocketOutlined,
    CompressOutlined,
    DatabaseOutlined,
    InfoCircleOutlined,
    FireOutlined,
    DashboardOutlined,
    TrophyOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;

const PerformanceCard = ({ settings, onUpdate }) => {
    const performanceScore = () => {
        const enabledFeatures = [
            settings.lazy_loading,
            settings.minify_css,
            settings.cache_styles
        ].filter(Boolean).length;
        return Math.round((enabledFeatures / 3) * 100);
    };

    const getScoreColor = (score) => {
        if (score >= 80) return '#52c41a';
        if (score >= 60) return '#faad14';
        return '#ff4d4f';
    };

    const getScoreStatus = (score) => {
        if (score >= 80) return { text: __('Excellent', 'marqueex'), status: 'success' };
        if (score >= 60) return { text: __('Good', 'marqueex'), status: 'warning' };
        return { text: __('Needs Improvement', 'marqueex'), status: 'error' };
    };

    const score = performanceScore();
    const scoreColor = getScoreColor(score);
    const scoreStatus = getScoreStatus(score);

    const cardStyle = {
        marginBottom: 24,
        borderRadius: 12,
        overflow: 'hidden',
        border: score > 60 ? `2px solid ${scoreColor}` : '1px solid #f0f0f0',
        boxShadow: score > 60 
            ? `0 4px 20px ${scoreColor}20` 
            : '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    };

    return (
        <Card
            style={cardStyle}
            bodyStyle={{ padding: 0 }}
            bordered={false}
        >
            {/* Header Section with Gradient */}
            <div style={{
                background: score > 60 
                    ? `linear-gradient(135deg, ${scoreColor} 0%, ${scoreColor}80 100%)` 
                    : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                padding: '20px 24px',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Background Pattern */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '100px',
                    height: '100px',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    transform: 'translate(30px, -30px)'
                }} />
                
                <Row align="middle" justify="space-between">
                    <Col>
                        <Space size="large" align="center">
                            <Avatar 
                                size={48}
                                style={{ 
                                    backgroundColor: score > 60 ? 'rgba(255,255,255,0.2)' : '#f0f0f0',
                                    color: score > 60 ? '#fff' : scoreColor,
                                    fontSize: '20px',
                                    border: score > 60 ? '2px solid rgba(255,255,255,0.3)' : 'none'
                                }}
                                icon={score >= 80 ? <TrophyOutlined /> : <DashboardOutlined />}
                            />
                            <div>
                                <Space size="small" align="center">
                                    <Text style={{ 
                                        fontSize: '18px', 
                                        fontWeight: 600, 
                                        color: score > 60 ? '#fff' : '#262626',
                                        textShadow: score > 60 ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                                    }}>
                                        {__('Performance Optimization', 'marqueex')}
                                    </Text>
                                    <Tag color={score >= 80 ? 'gold' : score >= 60 ? 'orange' : 'red'} style={{ margin: 0 }}>
                                        {score >= 80 ? __('Optimized', 'marqueex') : score >= 60 ? __('Enhanced', 'marqueex') : __('Basic', 'marqueex')}
                                    </Tag>
                                </Space>
                                <div style={{ marginTop: 4 }}>
                                    <Badge 
                                        status={scoreStatus.status} 
                                        text={
                                            <Text style={{ 
                                                color: score > 60 ? 'rgba(255,255,255,0.9)' : '#666',
                                                fontSize: '12px'
                                            }}>
                                                {scoreStatus.text}
                                            </Text>
                                        } 
                                    />
                                </div>
                            </div>
                        </Space>
                    </Col>
                    <Col>
                        <div style={{ textAlign: 'center' }}>
                            <Progress
                                type="circle"
                                size={50}
                                percent={score}
                                strokeColor={score > 60 ? '#fff' : scoreColor}
                                trailColor="rgba(255,255,255,0.3)"
                                strokeWidth={6}
                                format={() => (
                                    <div style={{ textAlign: 'center' }}>
                                        <Text style={{ 
                                            color: score > 60 ? '#fff' : scoreColor, 
                                            fontSize: '12px', 
                                            fontWeight: 600,
                                            display: 'block'
                                        }}>
                                            {score}%
                                        </Text>
                                        <Text style={{ 
                                            color: score > 60 ? 'rgba(255,255,255,0.8)' : '#999', 
                                            fontSize: '8px'
                                        }}>
                                            {__('Score', 'marqueex')}
                                        </Text>
                                    </div>
                                )}
                            />
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Content Section */}
            <div style={{ padding: '24px' }}>
                <div style={{ marginBottom: 20 }}>
                    <Paragraph style={{ 
                        marginBottom: 16, 
                        color: "#666", 
                        fontSize: '14px',
                        lineHeight: '1.6'
                    }}>
                        {__('Optimize MarqueeX performance with these advanced settings. Enable features to improve loading speed and reduce resource usage.', 'marqueex')}
                    </Paragraph>
                    
                    {/* Feature Tags */}
                    <div style={{ marginBottom: 16 }}>
                        <Text strong style={{ fontSize: '12px', color: '#999', marginBottom: 8, display: 'block' }}>
                            {__('Optimization Features:', 'marqueex')}
                        </Text>
                        <Space size={[4, 8]} wrap>
                            {['Speed Boost', 'Resource Optimization', 'Caching', 'Lazy Loading'].map((feature, index) => (
                                <Tag 
                                    key={index}
                                    style={{ 
                                        margin: 0,
                                        borderRadius: 12,
                                        fontSize: '11px',
                                        border: `1px solid ${scoreColor}20`,
                                        backgroundColor: `${scoreColor}10`,
                                        color: scoreColor
                                    }}
                                >
                                    {feature}
                                </Tag>
                            ))}
                        </Space>
                    </div>

                    {/* Performance Alert */}
                    {score < 60 && (
                        <Alert
                            message={__('Performance can be improved', 'marqueex')}
                            description={__('Enable more optimization features to boost your MarqueeX performance score.', 'marqueex')}
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16, borderRadius: 8 }}
                        />
                    )}
                </div>

                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#f0f9ff',
                            borderRadius: 12,
                            border: '2px solid #91d5ff',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                width: 40,
                                height: 40,
                                backgroundColor: '#1890ff',
                                borderRadius: '50%',
                                opacity: 0.1
                            }} />
                            <Row align="middle" justify="space-between">
                                <Col flex="1">
                                    <Space size="middle" align="center">
                                        <Avatar 
                                            size={40}
                                            style={{ 
                                                backgroundColor: '#1890ff20',
                                                color: '#1890ff',
                                                border: '2px solid #1890ff30'
                                            }}
                                            icon={<RocketOutlined />}
                                        />
                                        <div>
                                            <Text strong style={{ fontSize: '15px', display: 'block' }}>
                                                {__('Lazy Loading', 'marqueex')}
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {__('Load content only when visible to improve initial page load time', 'marqueex')}
                                            </Text>
                                            <div style={{ marginTop: 4 }}>
                                                <Tag color="blue" size="small">
                                                    {__('Recommended for performance', 'marqueex')}
                                                </Tag>
                                            </div>
                                        </div>
                                    </Space>
                                </Col>
                                <Col>
                                    <Switch
                                        checked={settings.lazy_loading}
                                        onChange={(checked) => onUpdate('lazy_loading', checked)}
                                        style={{ backgroundColor: settings.lazy_loading ? '#1890ff' : undefined }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Col>

                    <Col span={24}>
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#f6ffed',
                            borderRadius: 12,
                            border: '2px solid #b7eb8f',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                width: 40,
                                height: 40,
                                backgroundColor: '#52c41a',
                                borderRadius: '50%',
                                opacity: 0.1
                            }} />
                            <Row align="middle" justify="space-between">
                                <Col flex="1">
                                    <Space size="middle" align="center">
                                        <Avatar 
                                            size={40}
                                            style={{ 
                                                backgroundColor: '#52c41a20',
                                                color: '#52c41a',
                                                border: '2px solid #52c41a30'
                                            }}
                                            icon={<CompressOutlined />}
                                        />
                                        <div>
                                            <Text strong style={{ fontSize: '15px', display: 'block' }}>
                                                {__('Minify CSS', 'marqueex')}
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {__('Compress CSS files by removing whitespace and comments', 'marqueex')}
                                            </Text>
                                            <div style={{ marginTop: 4 }}>
                                                <Tag color="green" size="small">
                                                    {__('Reduces file size', 'marqueex')}
                                                </Tag>
                                            </div>
                                        </div>
                                    </Space>
                                </Col>
                                <Col>
                                    <Switch
                                        checked={settings.minify_css}
                                        onChange={(checked) => onUpdate('minify_css', checked)}
                                        style={{ backgroundColor: settings.minify_css ? '#52c41a' : undefined }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Col>

                    <Col span={24}>
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#f9f0ff',
                            borderRadius: 12,
                            border: '2px solid #d3adf7',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                width: 40,
                                height: 40,
                                backgroundColor: '#722ed1',
                                borderRadius: '50%',
                                opacity: 0.1
                            }} />
                            <Row align="middle" justify="space-between">
                                <Col flex="1">
                                    <Space size="middle" align="center">
                                        <Avatar 
                                            size={40}
                                            style={{ 
                                                backgroundColor: '#722ed120',
                                                color: '#722ed1',
                                                border: '2px solid #722ed130'
                                            }}
                                            icon={<DatabaseOutlined />}
                                        />
                                        <div>
                                            <Text strong style={{ fontSize: '15px', display: 'block' }}>
                                                {__('Cache Styles', 'marqueex')}
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {__('Store compiled styles in cache to avoid recompilation', 'marqueex')}
                                            </Text>
                                            <div style={{ marginTop: 4 }}>
                                                <Tag color="purple" size="small">
                                                    {__('Faster subsequent loads', 'marqueex')}
                                                </Tag>
                                            </div>
                                        </div>
                                    </Space>
                                </Col>
                                <Col>
                                    <Switch
                                        checked={settings.cache_styles}
                                        onChange={(checked) => onUpdate('cache_styles', checked)}
                                        style={{ backgroundColor: settings.cache_styles ? '#722ed1' : undefined }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

export default PerformanceCard;
